'use client';

import { useState, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

import { Button } from '@write4me/ui/components/button';
import { Input } from '@write4me/ui/components/input';
import { PasswordInput } from '@write4me/ui/components/password-input';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { useToast } from '@write4me/ui/components';
import { useSignInWithPassword } from '@/lib/query/hooks/use-auth-mutations';
import { createClient } from '@/lib/supabase/client';

export function LoginForm() {
  const router = useRouter();
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const signInMutation = useSignInWithPassword();
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);
  const showConfirmationAlert = searchParams.get('confirmed') === 'pending';
  const showResetSuccess = searchParams.get('reset') === 'success';
  // The invite page links here as `/login?invite=<token>` when a logged-out
  // visitor clicks "Sign in to join". This form used to ignore the param
  // entirely and route straight to a workspace or /invites, so the invitation
  // was orphaned and never accepted (audit AUTH-01).
  const inviteToken = searchParams.get('invite');

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const formData = new FormData(e.currentTarget);
    const email = (formData.get('email') as string | null)?.trim() ?? '';
    const password = (formData.get('password') as string | null) ?? '';

    if (!email) {
      setError('Please enter your email.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    signInMutation.mutate(
      { email, password },
      {
        onSuccess: async (data) => {
          // Check if user has completed onboarding
          const supabase = createClient();
          const userId = data.user?.id;

          if (userId) {
            const { data: profile } = await supabase
              .from('users')
              .select('onboarding_completed, role')
              .eq('id', userId)
              .single();

            if (!profile || !profile.onboarding_completed) {
              // Carry the token through onboarding so the invite page is still
              // reachable at the end of it.
              router.push(
                inviteToken
                  ? `/onboarding?invite=${encodeURIComponent(inviteToken)}`
                  : '/onboarding',
              );
              return;
            }

            // An in-flight invitation outranks the usual membership routing —
            // the user came here from the invite page to accept it.
            if (inviteToken) {
              router.push(`/invite/${encodeURIComponent(inviteToken)}`);
              return;
            }

            // Route directly to the user's primary destination to avoid an
            // intermediate /dashboard hop that depends on session cookies
            // propagating across the client→server boundary.
            //
            // Writers land on /invites unless they also manage a workspace
            // (owner/employer membership). A writer-role membership exists
            // when a writer was invited as a workspace member — the workspace
            // homepage is employer-focused and must not be shown to them.
            const membershipQuery = supabase
              .from('workspace_members')
              .select('role, workspaces(slug)')
              .eq('user_id', userId)
              .eq('status', 'active');

            if (profile.role === 'writer') {
              membershipQuery.in('role', ['owner', 'employer']);
            }

            const { data: memberships } = await membershipQuery.limit(1);

            type MembershipRow = {
              role: string;
              workspaces: { slug: string } | { slug: string }[] | null;
            };
            const first = (memberships as MembershipRow[] | null)?.[0];
            const ws = first?.workspaces;
            const slug = Array.isArray(ws) ? ws[0]?.slug : ws?.slug;

            if (slug) {
              router.push(`/workspace/${slug}`);
              return;
            }

            if (profile.role === 'employer') {
              router.push('/workspace/create');
            } else {
              router.push('/invites');
            }
            return;
          }

          router.push('/onboarding');
        },
        onError: (err) => {
          const raw = err.message ?? '';
          const friendly = friendlyAuthError(raw);
          setError(friendly);
          toast.error(friendly);
        },
      },
    );
  }

  function friendlyAuthError(raw: string): string {
    const msg = raw.toLowerCase();
    if (msg.includes('missing email') || msg.includes('missing phone')) {
      return 'Please enter your email and password.';
    }
    if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
      return 'That email and password don’t match an account. Please try again.';
    }
    if (msg.includes('email not confirmed')) {
      return 'Please confirm your email before signing in. Check your inbox for the confirmation link.';
    }
    if (msg.includes('rate limit')) {
      return 'Too many sign-in attempts. Please wait a moment and try again.';
    }
    return raw || 'Sign-in failed. Please try again.';
  }

  return (
    <div className="space-y-6">
      {showConfirmationAlert && (
        <div
          className="rounded-[12px] bg-secondary-container border border-secondary/20 px-4 py-3 text-sm font-ui text-ink"
          role="status"
        >
          Account created! Please check your email and confirm your address before signing in.
        </div>
      )}

      {showResetSuccess && (
        <div
          className="rounded-[12px] bg-secondary-container border border-secondary/20 px-4 py-3 text-sm font-ui text-ink"
          role="status"
        >
          Your password has been updated. Please sign in with your new password.
        </div>
      )}

      {/* Google sign-in leaves the app entirely, so the token has to ride
          along on the OAuth `next` or it is lost (AUTH-01). */}
      <GoogleSignInButton
        redirectTo={
          inviteToken
            ? `/invite/${encodeURIComponent(inviteToken)}`
            : undefined
        }
      />

      {/* Separator */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-outline-variant" />
        <span className="text-sm text-ink-muted font-ui">
          or continue with email
        </span>
        <div className="flex-1 h-px bg-outline-variant" />
      </div>

      {/* Inline server error (kept for accessibility; toast is additive) */}
      {error && (
        <div
          className="rounded-[12px] bg-primary-fixed border border-primary-light-border px-4 py-3 text-sm font-ui text-error"
          role="alert"
        >
          {error}
        </div>
      )}

      <form
        ref={formRef}
        method="post"
        action="/login"
        onSubmit={handleSubmit}
        className="space-y-4"
        noValidate
      >
        <Input
          label="Email"
          name="email"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          required
        />

        <PasswordInput
          label="Password"
          name="password"
          placeholder="Enter your password"
          autoComplete="current-password"
          required
        />

        <div className="flex justify-end -mt-2">
          <Link
            href="/forgot-password"
            className="text-sm font-ui text-primary font-medium hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={signInMutation.isPending}
          className="w-full"
        >
          Sign in
        </Button>
      </form>

      <p className="text-center text-sm font-ui text-ink-secondary">
        Don&apos;t have an account?{' '}
        <Link
          href="/signup"
          className="text-primary font-medium hover:underline"
        >
          Get started
        </Link>
      </p>
    </div>
  );
}
